import React from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionPlan } from '../../services/api';

interface SocialProofProps {
  /**
   * The paid plan whose real, runtime-sourced numbers back the stat strip.
   * Every stat is derived from genuine plan values - NEVER hardcoded marketing
   * figures (SPEC §5 H2). A stat is omitted entirely when its source value is
   * not available at runtime (no fabrication, no testimonials - Decision D3).
   */
  proPlan?: SubscriptionPlan | null;
}

interface Stat {
  value: string;
  label: string;
}

/**
 * Stat-based social proof strip for the pricing page. Pure presentational -
 * renders only the real numbers we can stand behind. Light/dark parity via
 * semantic theme tokens.
 */
const SocialProof: React.FC<SocialProofProps> = ({ proPlan }) => {
  const { t } = useTranslation();

  const stats: Stat[] = [];

  if (proPlan) {
    // Monthly AI credits - real entitlement number from the live plan catalog.
    if (typeof proPlan.aiCredits === 'number' && proPlan.aiCredits > 0) {
      stats.push({
        value: String(proPlan.aiCredits),
        label: t('pricing.statAiCredits', 'AI credits / month'),
      });
    }
    // Favorites cap - real entitlement number.
    if (typeof proPlan.maxFavorites === 'number' && proPlan.maxFavorites > 0) {
      stats.push({
        value: String(proPlan.maxFavorites),
        label: t('pricing.statFavorites', 'Saved favorites'),
      });
    }
    // Daily scans: only render when the backend actually sent the field.
    // `undefined` = data missing → omit (never fabricate "Unlimited").
    // Explicit `null` or `0` = backend signals no cap → "Unlimited".
    const scans = proPlan.maxScansDaily;
    if (scans !== undefined) {
      stats.push(
        scans === null || scans <= 0
          ? { value: t('pricing.statScansUnlimited', 'Unlimited'), label: t('pricing.statScans', 'Daily scans') }
          : { value: String(scans), label: t('pricing.statScans', 'Daily scans') }
      );
    }
  }

  // Nothing real to show → render nothing rather than invent stats.
  if (stats.length === 0) return null;

  return (
    <div
      className="flex flex-wrap justify-center gap-4 rounded-xl border border-border-subtle bg-surface-container px-3 py-4"
      role="group"
      aria-label={t('pricing.socialProofLabel', 'What Pro includes')}
    >
      {stats.map((s) => (
        <div key={s.label} className="px-2 text-center">
          <div className="font-display font-bold text-2xl leading-tight text-accent-primary">
            {s.value}
          </div>
          <div className="mt-1 text-sm text-text-secondary">{s.label}</div>
        </div>
      ))}
    </div>
  );
};

export default SocialProof;
